/**
 * O contrato entre a casca da Configurações e a tela embutida — K-4.
 *
 * ── O problema que isto resolve
 *
 * O K-2 embutiu as telas de configuração no painel da casca, e cada uma trouxe a
 * **página inteira**: `PageContainer`, `PageHeader`, breadcrumb e título
 * próprios. O resultado em `/admin/configuracoes/catalogos/tags` era a palavra
 * "Tags" **três vezes** na mesma tela e dois breadcrumbs se contradizendo
 * (`CONFIGURAÇÕES · CATÁLOGOS` da casca contra `CONFIGURAÇÕES · CADASTROS` da
 * tela antiga). O Matheus abriu e disse "está amador". Estava.
 *
 * ── Por que um contexto, e não uma prop em cada tela
 *
 * A alternativa óbvia era dar `embutido?: boolean` às 14 telas e espalhar `if`
 * por elas. Isso tem dois defeitos: mexe em 14 arquivos que não têm culpa, e
 * transfere para cada tela a decisão de como se comportar dentro da casca — que
 * é justamente o tipo de escolha que cada uma faria de um jeito.
 *
 * Com o contexto, quem se adapta são os **dois componentes canônicos**
 * (`PageHeader` e `PageContainer`), num lugar só. Tela nova migrada para a casca
 * já nasce certa, sem ninguém lembrar de passar prop nenhuma.
 *
 * ── O slot de ação
 *
 * "Novo departamento", "+ Nova tag", "Testar acesso" — decisão do Matheus: vão
 * para o cabeçalho da casca, **à direita, sempre no mesmo lugar**. A pessoa
 * aprende uma vez.
 *
 * A tela filha não precisa saber disso: ela continua passando `actions` ao seu
 * `PageHeader`, como sempre fez. Dentro da casca, o `PageHeader` **publica**
 * essas ações aqui em vez de desenhá-las. Nenhuma tela inventa posição, e o
 * botão não é duplicado.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { Ctx } from './casca-contrato';

export function CascaProvider({ children }: { children: ReactNode }) {
  const [acoes, setAcoes] = useState<ReactNode>(null);
  // `useCallback` não é zelo decorativo aqui: sem ele, a identidade da função
  // muda a cada render da casca, e o `useEffect` que publica no `PageHeader`
  // reagiria a isso — publicando em laço.
  const publicarAcoes = useCallback((a: ReactNode) => setAcoes(a), []);
  const valor = useMemo(
    () => ({ dentroDaCasca: true, acoes, publicarAcoes }),
    [acoes, publicarAcoes],
  );
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

